/**
 * Fetch helpers shared by every breakdown surface (drawer, full-page detail, actionables
 * row, the Breakdowns panel). Photo upload posts the file to this API, not a presigned S3
 * URL -- a presigned PUT is a cross-origin request straight to the bucket's own host, which
 * needs CORS configured ON THE BUCKET, and this deployment cannot set that (or even read
 * what's there) on the bucket in use. Proxying through this same-origin endpoint removes
 * that dependency entirely.
 */
import type {
  Breakdown, BreakdownIncident, BreakdownLogEntry, BreakdownPhoto, BreakdownReconcile,
} from './types'
import { notifyForbidden } from './notifyForbidden'

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null
    const msg = body?.error ?? `${fallback}: ${res.status} ${res.statusText}`
    if (res.status === 403) notifyForbidden(msg)
    throw new Error(msg)
  }
  return res.json() as Promise<T>
}

/** Direct clear -- no form, for the quick "Resolve" control on a card/row.
 *  resolvedTsMs: the operator's own answer to "when was it actually fixed" (epoch ms).
 *  Omitted, the server falls back to resumed_moving_ts (telemetry), then now(). */
export async function clearBreakdown(vehicleId: string, resolvedTsMs?: number): Promise<Breakdown> {
  const res = await fetch(`/api/vehicle/${encodeURIComponent(vehicleId)}/breakdown`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ clear: true, resolved_ts: resolvedTsMs ?? null }),
  })
  return jsonOrThrow<Breakdown>(res, 'could not resolve')
}

/** "That wasn't it" -- clears the resumed_moving_ts suggestion without resolving anything,
 *  for a false positive (a push, a tow test, one noisy GPS fix). Telemetry can raise it
 *  again on real, sustained movement. Returns the updated breakdown (same as clearBreakdown/
 *  updateBreakdownNote) so the caller can apply it locally instead of waiting on the SSE
 *  rebroadcast, which on the 2-pod deployment does not reliably reach the requesting browser. */
export async function dismissResumedMoving(vehicleId: string): Promise<Breakdown> {
  const res = await fetch(`/api/vehicle/${encodeURIComponent(vehicleId)}/breakdown/dismiss-moving`, {
    method: 'POST',
  })
  return jsonOrThrow<Breakdown>(res, 'could not dismiss')
}

/** Correct when a breakdown was actually reported -- only possible while it is still
 *  open; opened_ts otherwise never moves once set (see mark_breakdown on the server). */
export async function editBreakdownReportedAt(vehicleId: string, openedTsMs: number): Promise<void> {
  const res = await fetch(`/api/vehicle/${encodeURIComponent(vehicleId)}/breakdown/reported-at`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ opened_ts: openedTsMs }),
  })
  await jsonOrThrow(res, 'could not update reported time')
}

/** Edits the note on a still-open breakdown, in place -- no separate PATCH endpoint on
 *  the server; this is the same POST mark_breakdown uses for open/update, resent with
 *  everything else unchanged. It has to carry the OTHER fields too: mark_breakdown treats
 *  a missing category/severity as "Other"/"immobilised", not "leave alone", so sending
 *  {note} on its own would silently blank the fault type and severity along with it. */
export async function updateBreakdownNote(vehicleId: string, breakdown: Breakdown, note: string): Promise<Breakdown> {
  const res = await fetch(`/api/vehicle/${encodeURIComponent(vehicleId)}/breakdown`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      category: breakdown.category,
      severity: breakdown.severity,
      mobile: breakdown.mobile,
      eta_min: breakdown.eta_min,
      note,
    }),
  })
  return jsonOrThrow<Breakdown>(res, 'could not update note')
}

export async function fetchBreakdownHistory(vehicleId: string): Promise<BreakdownIncident[]> {
  const res = await fetch(`/api/vehicle/${encodeURIComponent(vehicleId)}/breakdown/history`)
  const body = await jsonOrThrow<{ incidents?: BreakdownIncident[] }>(res, 'could not load history')
  return body.incidents ?? []
}

export async function fetchBreakdowns(): Promise<Breakdown[]> {
  const res = await fetch('/api/breakdowns')
  const body = await jsonOrThrow<{ items?: Breakdown[] }>(res, 'could not load breakdowns')
  return body.items ?? []
}

export async function fetchBreakdownLog(
  limit = 100,
  range?: { from?: string; to?: string },
): Promise<{ incidents: BreakdownLogEntry[]; medianTatMin: number | null; resolvedCount: number }> {
  const q = new URLSearchParams({ limit: String(limit) })
  if (range?.from) q.set('from', range.from)
  if (range?.to) q.set('to', range.to)
  const res = await fetch(`/api/breakdowns/log?${q}`)
  const body = await jsonOrThrow<{
    incidents?: BreakdownLogEntry[]; median_tat_min?: number | null; resolved_count?: number
  }>(res, 'could not load breakdown log')
  return {
    incidents: body.incidents ?? [],
    medianTatMin: body.median_tat_min ?? null,
    resolvedCount: body.resolved_count ?? 0,
  }
}

/** Correct a breakdown record after the fact -- active or long resolved. Admin-only on
 *  the server (see edit_breakdown); only present the affordance to admins in the UI too,
 *  so a non-admin never sees an edit control that would just 403. */
export async function editBreakdown(
  breakdownId: number,
  changes: {
    category?: string; severity?: string; note?: string
    openedTsMs?: number; clearedTsMs?: number
    lat?: number; lon?: number
  },
): Promise<void> {
  const res = await fetch(`/api/breakdown/${breakdownId}/edit`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      category: changes.category, severity: changes.severity, note: changes.note,
      opened_ts: changes.openedTsMs, cleared_ts: changes.clearedTsMs,
      lat: changes.lat, lon: changes.lon,
    }),
  })
  await jsonOrThrow(res, 'could not save changes')
}

/** Read-only cross-check against the vehicle's own geofence-crossing history -- never
 *  changes the record. Admin-only on the server, same reasoning as editBreakdown. */
export async function reconcileBreakdown(breakdownId: number): Promise<BreakdownReconcile> {
  const res = await fetch(`/api/breakdown/${breakdownId}/reconcile`)
  return jsonOrThrow<BreakdownReconcile>(res, 'could not reconcile')
}

export async function uploadBreakdownPhoto(
  vehicleId: string, breakdownId: number, file: File,
): Promise<BreakdownPhoto> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch(
    `/api/vehicle/${encodeURIComponent(vehicleId)}/breakdown/${breakdownId}/photo`,
    { method: 'POST', body: form },
  )
  return jsonOrThrow<BreakdownPhoto>(res, 'could not upload photo')
}
